/**
 * AssistantChat — the rich chat UI for the in-app preview Assistant.
 *
 * Streams a turn from `/api/assistant/ask/stream` and renders rich message parts
 * as they arrive — text, an inline auto-chosen chart (<InsightVisual>), a result
 * table, or a proposed-ACTION card the user reviews and confirms. The Assistant
 * never writes data directly: it proposes an action (add / edit / delete / a
 * custom action); the user reviews the values and clicks Run, and the server
 * runs it through the object's API ONLY if the user is authorized. This is display.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CSSProperties } from 'react'
import { MarkdownText } from '@twasta/page-runtime/markdown'
import { ComposedContentCard, describeRequestError, type AuthoringInfo } from '@twasta/page-runtime/assistant/ComposedContentCard'
import { QuestionsCard, formatAnswers, type AssistantQuestion } from '@twasta/page-runtime/assistant/QuestionsCard'
import { MentionInput, type MentionGroups } from '@twasta/page-runtime/shell'
import {
  documentTarget, recordTarget, screenTarget,
  type OpenTarget, type OpenTargetHandler,
} from '@twasta/page-runtime/search'
import { detectFileKind, displayNameForUrl } from '@twasta/page-runtime/txn/widgets/fileKind'
import { InsightVisual } from '@twasta/visual-engine'
import type { VisualDataPayload, VisualJsonData, VisualLayout, VisualOption } from '@twasta/visual-engine'
import {
  assistantAskStream, newAssistantConversationId, assistantRunAction, assistantRunIntent, assistantConfig, assistantSaveVisual,
  assistantImportUpload, assistantComposeRefine, assistantImportUnlock,
  type AssistantConfig, type AssistantEvent, type AssistantIntentRunResult, type AssistantRunResult,
  type AssistantImportUpload, type ImportCounts, type ImportIssue,
} from '@/api/assistant'
import { fetchVisualData } from '@/api/visualData'
import { livePreviewExecute } from '@/livepreview/api'
import { runReportJob } from '@/api/reportData'
import { VisualFullscreen } from './SavedVisuals'
import { ScanCapture } from './ScanCapture'
import { ImportPreview, type ImportOutcome } from './ImportPreview'

interface PreviewColumn { key: string; label: string; type?: string; required?: boolean; auto?: boolean; rich?: boolean; long?: boolean; maxlen?: number }

type Part =
  | { kind: 'text'; text: string }
  | { kind: 'visual'; jsonData: VisualJsonData; visualLayout: VisualLayout; optionsArr: VisualOption[]; chartType?: string; title?: string; businessArea?: string; spec?: Record<string, unknown>; question?: string }
  | { kind: 'rows'; columns: string[]; columnLabels?: Record<string, string>; rows: Record<string, unknown>[]; rowCount: number }
  | { kind: 'action'; actionId: string; objName: string; objLabel?: string; action: string; isCustom: boolean; summary: string; columns: PreviewColumn[]; values: Record<string, unknown>; ran?: 'ok' | 'error'; ranValues?: Record<string, unknown>; result?: Record<string, unknown>; runError?: string
      // Set when the Assistant WROTE this record's content (compose_content)
      // or the object has a draft/live lifecycle: drawn as the review card.
      authoring?: AuthoringInfo; ranPublished?: boolean }
  | { kind: 'link'; target: string; title: string }
  // Questions the Assistant asked (ask_user), each with its suggested answer
  // pre-filled. `intentId` = a form-button intent's missing parameters: the
  // answers re-run that intent instead of becoming a chat message.
  | { kind: 'questions'; qid: string; intro?: string; questions: AssistantQuestion[]; answered?: Record<string, string>; intentId?: string; parameters?: Record<string, unknown> }
  // Documents the answer was drawn from. Each one opens the document itself,
  // in the repository it lives in — an answer about a controlled document is
  // only usable if the reader can get to the release it came from.
  | { kind: 'documents'; citations: DocCitation[] }
  // Records the answer identified by name ("the artist ARR"). Each one
  // opens the record itself — the same screen, in the same state,
  // application-wide search would have opened it on.
  | { kind: 'records'; citations: RecordCitation[] }
  // A DEFINED Visual (V) / Jasper Report (R) referenced by an intent — the
  // client fetches it through the secured visual-data / report-data routes.
  | { kind: 'visual_ref'; objName: string; params: Record<string, unknown>; title?: string }
  | { kind: 'report'; objName: string; params: Record<string, unknown>; title?: string }
  // A deterministic intent run (form button / seeded modal) awaiting the
  // user's confirmation before it executes.
  | { kind: 'intent_confirm'; cid: string; intentId: string; parameters: Record<string, unknown>; prompt: string; resolved?: 'ran' | 'declined' }
  // Data files the user attached to a message (already staged on the server).
  | { kind: 'files'; files: AssistantImportUpload[] }
  // A file prepared for import — opens the side-by-side spreadsheet preview.
  // `outcome` is what the user did there, carried into the next turn.
  | { kind: 'import_preview'; cardId: string; importId: string; objName: string; objLabel?: string; mode: string; filename?: string; summary?: string; counts: ImportCounts; topIssues: ImportIssue[]; outcome?: ImportOutcome }
  // A password-protected PDF: the user types its password into this card —
  // it goes straight to the unlock endpoint, never into the conversation.
  | { kind: 'import_password'; cardId: string; importId: string; objName: string; sender?: string; canRemember?: boolean; unlocked?: boolean; note?: string }

/** One document the answer quoted. `repo_obj` is stamped on by the server so
 *  the client can build a link; without it the citation is shown but not
 *  clickable, rather than linking somewhere wrong. */
interface DocCitation {
  document_id: string
  repo_obj?: string
  title?: string
  version?: string
  effective_date?: string
  page?: number | null
  excerpt?: string
}

/** One record the Assistant resolved. `record_key` IS the executor's
 *  `txn_id` — the handle the record editor and the search index both
 *  address a row by. `openable` false means the object is indexed as a
 *  table source and has no editor, so it is named but not linked. */
interface RecordCitation {
  obj_name: string
  record_key: string
  title: string
  subtitle?: string
  openable?: boolean
}

interface Msg { role: 'user' | 'assistant'; parts: Part[] }

// Fields the ActionCard reports back once the user runs (or fails to run) a
// proposed action, so the outcome is recorded on the message and carried into
// the next turn's history.
type ActionRunOutcome = Pick<Extract<Part, { kind: 'action' }>, 'ran' | 'ranValues' | 'result' | 'runError' | 'ranPublished'>

// Long text (a drafted article) is cut down in the history note: the model
// re-reads every note on every later turn, and it never needs the full text.
function historyValues(values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(values || {})) {
    if (typeof v === 'string' && v.length > 240) {
      const text = v.includes('<') ? v.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : v
      out[k] = `${text.slice(0, 240).trimEnd()}… (${v.length} chars)`
    } else out[k] = v
  }
  return out
}

// Flatten a message part into a one-line history record for the model. Text is
// verbatim; the other parts (which the model can't otherwise see on later turns)
// become a bracketed note. For a run action the note carries the SUBMITTED values
// and the engine result (which includes the new/edited record's key) — so a
// follow-up like "change the company name in this lead" can be handled as an
// EDIT of the record just created, instead of a fresh, half-filled add.
function serializePartForHistory(p: Part): string {
  if (p.kind === 'text') return p.text
  if (p.kind === 'action') {
    const vals = JSON.stringify(historyValues(p.ranValues || p.values || {}))
    if (p.ran === 'ok') {
      const how = p.authoring?.status_column ? (p.ranPublished ? ' (PUBLISHED by the user)' : ' (saved as a DRAFT)') : ''
      return `[Action performed: '${p.action}' on ${p.objName} SUCCEEDED${how}. Submitted values: ${vals}. `
        + `Result (includes the record's key/id): ${JSON.stringify(p.result ?? {})}]`
    }
    if (p.ran === 'error') {
      return `[Action '${p.action}' on ${p.objName} was run but FAILED: ${p.runError || 'unknown error'}. Submitted values: ${vals}]`
    }
    return `[Proposed '${p.action}' on ${p.objName} (NOT yet run — awaiting the user to review and click Run). Proposed values: ${vals}]`
  }
  if (p.kind === 'visual') return `[Rendered a ${p.chartType || 'chart'}${p.title ? ` titled "${p.title}"` : ''}.]`
  if (p.kind === 'rows') return `[Showed a table of ${p.rowCount} row(s); columns: ${p.columns.join(', ')}.]`
  if (p.kind === 'link') return `[Offered a link to "${p.title}".]`
  if (p.kind === 'documents') {
    return `[Cited ${p.citations.length} document(s): `
      + p.citations.map((c) => c.title || c.document_id).join(', ') + '.]'
  }
  if (p.kind === 'records') {
    return `[Offered ${p.citations.length} record(s) to open: `
      + p.citations.map((c) => `${c.title || c.record_key} (${c.obj_name})`).join(', ') + '.]'
  }
  if (p.kind === 'visual_ref') return `[Rendered the defined visual "${p.title || p.objName}" inline.]`
  if (p.kind === 'report') return `[Rendered the report "${p.title || p.objName}" inline.]`
  if (p.kind === 'files') {
    return p.files.map((f) => `[Attached data file "${f.filename}" (import_id=${f.import_id}, `
      + `${f.rows} row(s), ${f.columns} column(s)).]`).join('\n')
  }
  if (p.kind === 'import_preview') {
    const c = p.counts
    const base = `[Prepared import preview of "${p.filename || p.importId}" (import_id=${p.importId}) into ${p.objName}, `
      + `mode ${p.mode}: ${c.records} record(s) — ${c.new} new, ${c.update} update(s)`
      + `${c.unchanged ? `, ${c.unchanged} unchanged (identical to the stored record, nothing to save)` : ''}`
      + `; ${c.errors} error(s), ${c.warnings} warning(s).`
    if (!p.outcome) return `${base} The user has not saved it yet.]`
    const o = p.outcome
    return `${base} In the preview the user SAVED ${o.saved} of ${o.total}`
      + `${o.failed ? `; ${o.failed} failed validation` : ''}${o.pending ? `; ${o.pending} not saved` : ''}`
      + `${o.unchanged ? `; ${o.unchanged} were already up to date` : ''}`
      + `${o.leftOut ? `; the user LEFT OUT ${o.leftOut} (not imported — do not retry them unless asked)` : ''}.]`
  }
  if (p.kind === 'import_password') {
    return p.unlocked
      ? `[The user unlocked the password-protected PDF (import_id=${p.importId}) for ${p.objName}.]`
      : `[The PDF (import_id=${p.importId}) is password-protected; a password box was shown to the user.]`
  }
  if (p.kind === 'questions') {
    const qs = p.questions.map((q) => `${q.question} (suggested: ${q.suggested || '—'})`).join('; ')
    return p.answered ? `[Asked the user: ${qs} — they answered, see their next message.]`
      : `[Asked the user: ${qs} — awaiting their answers.]`
  }
  if (p.kind === 'intent_confirm') {
    const state = p.resolved === 'ran' ? 'the user confirmed and it ran'
      : p.resolved === 'declined' ? 'the user declined' : 'awaiting the user'
    return `[Asked to confirm intent ${p.intentId}: "${p.prompt}" — ${state}.]`
  }
  return ''
}

const ACTIVITY: Record<string, string> = {
  query_business_area: 'Looking up your data…',
  render_visual: 'Building a chart…',
  propose_action: 'Preparing a change…',
  compose_content: 'Writing the draft…',
  ask_user: 'Preparing questions…',
  execute_intent: 'Serving your request…',
  inspect_data_file: 'Analysing the file (a document is read first)…',
  prepare_data_import: 'Preparing the import preview…',
}

// What the attach button accepts — the server re-checks.
// Data files are imported as they are; documents (PDF, a photo or scan, Word)
// are READ by the AI into rows first.
const IMPORT_ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm,.xls,.ods,.json,.pdf,.png,.jpg,.jpeg,.webp,.docx'


export interface AssistantChatProps {
  project: string
  businessArea?: string
  title?: string
  greeting?: string
  suggestedQuestions?: string[]
  height?: number | string
  style?: React.CSSProperties
  /** Designer "preview as this profile" (Configure AI → Preview): the turn runs
   *  with these functional roles instead of the caller's own, so the designer
   *  sees exactly what that profile is served. Requires project edit rights. */
  asRoles?: string[]
  /** The object the reader has OPEN. A document repository on screen narrows
   *  document search to that repository — see `obj_name` in the ask body. */
  objName?: string
  /** Auto-send this message once when the chat opens (transaction-form
   *  'assistant' action buttons in prompt mode seed the turn with it). */
  initialMessage?: string
  /** Run this DEFINED INTENT deterministically (no LLM) once when the chat
   *  opens (transaction-form 'assistant' action buttons in intent mode). */
  initialIntent?: { intentId: string; parameters?: Record<string, unknown>; label?: string }
  /** How this host opens a citation. Given, a record/document/screen
   *  citation navigates in place exactly as an application-wide search
   *  result does — the SAME `OpenTarget` the search panel produces.
   *  Omitted (a designer preview, a smart-page embed), citations fall
   *  back to an ordinary link derived from the current URL. */
  onOpen?: OpenTargetHandler
}

export function AssistantChat({
  project, businessArea, title, greeting, suggestedQuestions, height = '100%', style, asRoles,
  initialMessage, initialIntent, onOpen, objName,
}: AssistantChatProps) {
  const [config, setConfig] = useState<AssistantConfig | null>(null)
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [activity, setActivity] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  // Data files attached to the NEXT message (staged on upload).
  const [attached, setAttached] = useState<AssistantImportUpload[]>([])
  const [uploading, setUploading] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // The import preview on screen (one at a time).
  const [preview, setPreview] = useState<{ cardId: string; importId: string; objLabel?: string } | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // In-flight stream controller — lets the user interrupt (Stop) a running turn.
  const abortRef = useRef<AbortController | null>(null)
  // The question that drove the current turn — stored on any visual it renders
  // so a Save has a sensible default title.
  const lastQuestionRef = useRef('')
  // One id for this conversation — what a call_ioflow intent reports as UUID.
  const conversationIdRef = useRef(newAssistantConversationId())

  // eslint-disable-next-line react-hooks/exhaustive-deps -- asRoles is a stable prop array per preview session
  const asRolesKey = (asRoles || []).join(',')
  useEffect(() => {
    assistantConfig(project, asRoles).then(setConfig).catch(() => setConfig({ enabled: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, asRolesKey])

  /**
   * What an `@` offers: the app's people, its roles, and the master records it
   * has declared semantically searchable. Best-effort — a project that serves
   * no mention endpoint (or a viewer with no standing in it) simply gets a
   * plain input back, which is the same composer as before.
   */
  const mentionSearch = useCallback(async (q: string): Promise<MentionGroups> => (
    await livePreviewExecute(project, '_worklist', 'worklist_mentions',
                             { q, limit: 8 }) as MentionGroups
  ), [project])

  // Seed the greeting once config (or the prop) is known.
  useEffect(() => {
    const g = greeting ?? config?.greeting
    if (g && messages.length === 0) setMessages([{ role: 'assistant', parts: [{ kind: 'text', text: g }] }])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, activity])

  // Mutate the trailing assistant message's parts (the one we're streaming into).
  const patchLast = (fn: (parts: Part[]) => Part[]) => {
    setMessages((m) => {
      if (!m.length) return m
      const last = m[m.length - 1]
      if (last.role !== 'assistant') return m
      return [...m.slice(0, -1), { ...last, parts: fn(last.parts) }]
    })
  }
  const appendText = (text: string) => patchLast((parts) => {
    const tail = parts[parts.length - 1]
    if (tail && tail.kind === 'text') return [...parts.slice(0, -1), { kind: 'text', text: (tail.text ? tail.text + '\n' : '') + text }]
    return [...parts, { kind: 'text', text }]
  })
  // Streamed narration chunk (CLI engines): append RAW to the trailing text
  // part — chunks are token deltas, not blocks — starting a new bubble when
  // the server marks a message boundary (or a payload card intervened).
  const appendDelta = (text: string, newBlock?: boolean) => patchLast((parts) => {
    const tail = parts[parts.length - 1]
    if (!newBlock && tail && tail.kind === 'text') return [...parts.slice(0, -1), { kind: 'text', text: tail.text + text }]
    return [...parts, { kind: 'text', text }]
  })

  const appendPayload = (p: Record<string, unknown>) => {
    if (p.kind === 'visual') {
      patchLast((parts) => [...parts, {
        kind: 'visual',
        jsonData: p.jsonData as VisualJsonData,
        visualLayout: p.visualLayout as VisualLayout,
        optionsArr: (p.optionsArr as VisualOption[]) || [],
        chartType: p.chartType as string | undefined,
        title: p.title as string | undefined,
        businessArea: p.businessArea as string | undefined,
        spec: p.spec as Record<string, unknown> | undefined,
        question: lastQuestionRef.current,
      }])
    } else if (p.kind === 'questions') {
      patchLast((parts) => [...parts, {
        kind: 'questions',
        qid: (p.question_id as string) || Math.random().toString(36).slice(2, 10),
        intro: (p.intro as string) || undefined,
        questions: (p.questions as AssistantQuestion[]) || [],
      }])
    } else if (p.kind === 'rows') {
      patchLast((parts) => [...parts, {
        kind: 'rows',
        columns: (p.columns as string[]) || [],
        columnLabels: (p.columnLabels as Record<string, string>) || undefined,
        rows: (p.rows as Record<string, unknown>[]) || [],
        rowCount: (p.rowCount as number) || 0,
      }])
    } else if (p.kind === 'action') {
      patchLast((parts) => [...parts, {
        kind: 'action',
        actionId: p.action_id as string,
        objName: p.obj_name as string,
        objLabel: p.obj_label as string | undefined,
        action: (p.action as string) || '',
        isCustom: !!p.is_custom,
        summary: (p.summary as string) || '',
        columns: (p.columns as PreviewColumn[]) || [],
        values: (p.values as Record<string, unknown>) || {},
        authoring: (p.authoring as AuthoringInfo | undefined) || undefined,
      }])
    } else if (p.kind === 'documents') {
      // Was silently dropped: the server has always sent these, and the chat
      // showed the answer with no way to reach what it was based on.
      const citations = (p.citations as DocCitation[]) || []
      if (citations.length) {
        patchLast((parts) => [...parts, { kind: 'documents', citations }])
      }
    } else if (p.kind === 'master_matches') {
      // The resolver already found the exact rows the answer is about;
      // until now only the model saw them. Surfacing them gives the
      // reader the one thing the prose cannot: the record itself.
      const matches = (p.matches as Record<string, unknown>[]) || []
      const citations: RecordCitation[] = matches
        .map((m) => ({
          obj_name: String(m.obj_name || ''),
          record_key: m.row_key == null ? '' : String(m.row_key),
          title: String(m.display || m.key_value || '').trim(),
          subtitle: String(m.key_value || '').trim(),
          openable: m.openable !== false,
        }))
        .filter((c) => c.obj_name && c.record_key)
      if (citations.length) patchLast((parts) => [...parts, { kind: 'records', citations }])
    } else if (p.kind === 'link') {
      // A navigate-intent link: routes the user to a screen in this app.
      const target = String(p.target || '')
      if (target) patchLast((parts) => [...parts, { kind: 'link', target, title: String(p.title || target) }])
    } else if (p.kind === 'import_preview') {
      const importId = String(p.import_id || '')
      if (importId) {
        patchLast((parts) => [...parts, {
          kind: 'import_preview',
          cardId: Math.random().toString(36).slice(2, 10),
          importId,
          objName: String(p.obj_name || ''),
          objLabel: p.obj_label as string | undefined,
          mode: String(p.mode || 'add'),
          filename: p.filename as string | undefined,
          summary: p.summary as string | undefined,
          counts: (p.counts as ImportCounts) || { records: 0, new: 0, update: 0, errors: 0, warnings: 0 },
          topIssues: (p.top_issues as ImportIssue[]) || [],
        }])
      }
    } else if (p.kind === 'import_password') {
      const importId = String(p.import_id || '')
      if (importId) {
        patchLast((parts) => [...parts, {
          kind: 'import_password', cardId: Math.random().toString(36).slice(2, 10), importId,
          objName: String(p.obj_name || ''), sender: (p.sender as string | undefined) || undefined,
          canRemember: p.can_remember !== false,
        }])
      }
    } else if (p.kind === 'visual_ref' || p.kind === 'report') {
      // Intent-referenced defined Visual / Report — fetched lazily by its card
      // through the secured visual-data / report-data routes.
      const objName = String(p.obj_name || '')
      if (objName) {
        patchLast((parts) => [...parts, {
          kind: p.kind as 'visual_ref' | 'report', objName,
          params: (p.params as Record<string, unknown>) || {},
          title: p.title as string | undefined,
        }])
      }
    }
  }

  // Record a proposed action's run outcome onto its message part, so it persists
  // and is carried into the next turn's history (continuity for follow-ups).
  const recordActionRun = useCallback((actionId: string, outcome: ActionRunOutcome) => {
    setMessages((ms) => ms.map((m) => ({
      ...m,
      parts: m.parts.map((p) => (p.kind === 'action' && p.actionId === actionId) ? { ...p, ...outcome } : p),
    })))
  }, [])

  // Stage each file at once so an unreadable one is refused before the turn.
  const attachFiles = async (files: FileList | File[] | null) => {
    const list = Array.from(files || [])
    if (!list.length) return
    setError(null)
    for (const f of list) {
      setUploading((n) => n + 1)
      try {
        const up = await assistantImportUpload(project, f)
        setAttached((a) => [...a.filter((x) => x.import_id !== up.import_id), up].slice(-5))
      } catch (e) {
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setError(detail || `Couldn't attach ${f.name}.`)
      } finally {
        setUploading((n) => n - 1)
      }
    }
  }

  // Record what the user did in the import preview onto its card, so the next
  // turn knows which rows were saved.
  const recordImportOutcome = useCallback((cardId: string, outcome: ImportOutcome) => {
    setMessages((ms) => ms.map((m) => ({
      ...m,
      parts: m.parts.map((p) => (p.kind === 'import_preview' && p.cardId === cardId) ? { ...p, outcome } : p),
    })))
  }, [])

  const send = async (text: string, opts?: { scan?: boolean }) => {
    const files = opts?.scan ? [] : attached
    const q = text.trim() || (files.length ? `Import the attached file${files.length > 1 ? 's' : ''}.` : '')
    if (!q || busy || uploading > 0) return
    lastQuestionRef.current = q
    setError(null); setInput('')
    // A scanned code shows as a scan bubble; the server receives it BOTH as the
    // turn's user message and as the `scan` field that drives intent routing.
    const userParts: Part[] = [{ kind: 'text', text: opts?.scan ? `📷 ${q}` : q }]
    if (files.length) userParts.push({ kind: 'files', files })
    setAttached([])
    // One history entry per message, preserving the order of text + cards within
    // a turn — so the model sees the actions it proposed/ran, not just its prose.
    const history = messages
      .map((m) => ({
        role: m.role,
        content: m.parts.map(serializePartForHistory).filter(Boolean).join('\n').trim(),
      }))
      .filter((h) => h.content !== '')
    setMessages((m) => [...m, { role: 'user', parts: userParts }, { role: 'assistant', parts: [] }])
    setBusy(true); setActivity('Thinking…')
    const controller = new AbortController()
    abortRef.current = controller
    let gotText = false
    try {
      await assistantAskStream(project, {
        messages: [...history, {
          role: 'user',
          content: [opts?.scan ? `[Scanned code] ${q}` : q,
            ...(files.length ? [serializePartForHistory({ kind: 'files', files })] : [])].join('\n'),
        }],
        imports: files.length ? files.map((f) => f.import_id) : undefined,
        business_area: businessArea,
        conversation_id: conversationIdRef.current,
        as_roles: asRoles?.length ? asRoles : undefined,
        scan: opts?.scan ? q : undefined,
        obj_name: objName || undefined,
      }, (ev: AssistantEvent) => {
        if (ev.type === 'text' && ev.text) { gotText = true; setActivity(''); appendText(ev.text) }
        else if (ev.type === 'text_delta' && ev.text) { gotText = true; setActivity(''); appendDelta(ev.text, ev.new_block) }
        else if (ev.type === 'activity') setActivity(ev.text || 'Working…')
        else if (ev.type === 'tool_start') setActivity(ACTIVITY[ev.name || ''] || ev.summary || 'Working…')
        else if (ev.type === 'tool_end') { setActivity(''); if (ev.payload) appendPayload(ev.payload) }
        else if (ev.type === 'done') { setActivity(''); if (!gotText && ev.reply) appendText(ev.reply) }
        else if (ev.type === 'error') setError(ev.detail || 'The assistant ran into a problem.')
      }, controller.signal)
    } catch (e) {
      // A user-initiated Stop surfaces as an AbortError — note it inline rather
      // than as an error banner.
      if (controller.signal.aborted) appendText('⏹ Stopped.')
      else setError(e instanceof Error ? e.message : 'The assistant is unavailable.')
    } finally {
      if (abortRef.current === controller) abortRef.current = null
      setBusy(false); setActivity('')
    }
  }

  // Interrupt the running turn — aborts the fetch stream; whatever the assistant
  // had already streamed stays on screen.
  const stop = () => { abortRef.current?.abort() }

  // Auto-send the seeded message once (transaction-form assistant buttons in
  // prompt mode open the chat with the rendered prompt).
  const sentInitialRef = useRef(false)
  useEffect(() => {
    if (!initialMessage?.trim() || sentInitialRef.current || config == null) return
    sentInitialRef.current = true
    if (!messages.some((m) => m.role === 'user')) send(initialMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, initialMessage])

  // Run one DEFINED INTENT deterministically (no LLM) and append its result as
  // an assistant message. Confirmation-gated intents render a confirm card the
  // user answers (see IntentConfirmCard); the confirmed re-run lands here too.
  const runIntent = async (intentId: string, parameters: Record<string, unknown>, confirmed?: boolean) => {
    if (busy) return
    setError(null)
    setMessages((m) => [...m, { role: 'assistant', parts: [] }])
    setBusy(true); setActivity('Working…')
    try {
      const r: AssistantIntentRunResult = await assistantRunIntent(project, {
        intent_id: intentId, parameters, confirmed,
        conversation_id: conversationIdRef.current,
        as_roles: asRoles?.length ? asRoles : undefined,
      })
      if (r.needs_confirmation && r.prompt) {
        const cid = Math.random().toString(36).slice(2, 10)
        patchLast((parts) => [...parts, { kind: 'intent_confirm', cid, intentId, parameters, prompt: r.prompt! }])
      } else if (!r.ok && r.questions?.length) {
        // Missing parameters: the same pre-filled question form the chat uses.
        patchLast((parts) => [...parts, {
          kind: 'questions', qid: Math.random().toString(36).slice(2, 10),
          intro: 'A few details first — the suggested values are filled in:',
          questions: r.questions!, intentId: r.intent_id || intentId,
          parameters: { ...(r.parameters || parameters) },
        }])
      } else if (!r.ok) {
        patchLast((parts) => [...parts, { kind: 'text', text: r.error || 'The request failed.' }])
      } else {
        for (const p of r.payloads || []) appendPayload(p)
        if (r.reply) appendText(r.reply)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The assistant is unavailable.')
    } finally {
      setBusy(false); setActivity('')
    }
  }

  // Answer the Assistant's questions: mark the card answered, then either
  // re-run the form-button intent with the values or send them as the
  // person's next message.
  /** Unlock a password-protected PDF from its card, then let the Assistant
   *  carry on. Resolves with an error message to show on the card, or null. */
  const unlockImport = async (part: Extract<Part, { kind: 'import_password' }>,
    password: string, remember: boolean): Promise<string | null> => {
    try {
      const res = await assistantImportUnlock(project, part.importId, {
        obj_name: part.objName, password, remember, sender: part.sender,
      })
      const note = res.password?.remembered ? 'Password remembered for this sender.' : (res.password?.note || '')
      setMessages((ms) => ms.map((m) => ({
        ...m,
        parts: m.parts.map((p) => (p.kind === 'import_password' && p.cardId === part.cardId)
          ? { ...p, unlocked: true, note } : p),
      })))
      void send('I unlocked the password-protected PDF — please continue the import.')
      return null
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      if (detail && typeof detail === 'object' && (detail as { message?: string }).message) {
        return (detail as { message: string }).message
      }
      console.warn('[AssistantChat] could not unlock the PDF', e)
      return typeof detail === 'string' ? detail : 'The PDF could not be unlocked.'
    }
  }

  const answerQuestions = (qid: string, answers: Record<string, string>) => {
    const target = messages.flatMap((m) => m.parts)
      .find((p): p is Extract<Part, { kind: 'questions' }> =>
        p.kind === 'questions' && p.qid === qid && !p.answered)
    if (!target || busy) return
    setMessages((ms) => ms.map((m) => ({
      ...m,
      parts: m.parts.map((p) => (p.kind === 'questions' && p.qid === qid) ? { ...p, answered: { ...answers } } : p),
    })))
    if (target.intentId) {
      runIntent(target.intentId, { ...(target.parameters || {}), ...answers })
    } else {
      void send(formatAnswers(target.questions, answers))
    }
  }

  // Mark a confirm card answered, then run (or drop) the intent.
  const answerIntentConfirm = (cid: string, run: boolean) => {
    const target = messages.flatMap((m) => m.parts)
      .find((p): p is Extract<Part, { kind: 'intent_confirm' }> =>
        p.kind === 'intent_confirm' && p.cid === cid && !p.resolved)
    if (!target) return
    setMessages((ms) => ms.map((m) => ({
      ...m,
      parts: m.parts.map((p) => (p.kind === 'intent_confirm' && p.cid === cid)
        ? { ...p, resolved: run ? 'ran' as const : 'declined' as const } : p),
    })))
    if (run) runIntent(target.intentId, target.parameters, true)
  }

  // Run the seeded intent once (transaction-form assistant buttons in intent
  // mode). Works without an AI configured — the run is deterministic.
  const sentInitialIntentRef = useRef(false)
  useEffect(() => {
    if (!initialIntent?.intentId || sentInitialIntentRef.current || config == null) return
    sentInitialIntentRef.current = true
    setMessages((m) => [...m, {
      role: 'user',
      parts: [{ kind: 'text', text: initialIntent.label || `▶ ${initialIntent.intentId}` }],
    }])
    runIntent(initialIntent.intentId, initialIntent.parameters || {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, initialIntent])

  const chips = (suggestedQuestions ?? config?.suggested_questions) || []
  const hasUser = messages.some((m) => m.role === 'user')
  // The Assistant is granted to this user but no AI is configured for their
  // organization yet (a func-admin has to set it up in System administration →
  // AI). Say so plainly instead of letting every question fail with a 503.
  const aiUnconfigured = config != null && config.ai_configured === false
  const canImport = !!config?.capabilities?.includes('import')

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', height, minHeight: 0, background: '#fff', color: '#1f2937',
        ...(dragOver ? { outline: '2px dashed #4f46e5', outlineOffset: -4 } : {}), ...style,
      }}
      onDragOver={canImport ? (e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true) } } : undefined}
      onDragLeave={canImport ? () => setDragOver(false) : undefined}
      onDrop={canImport ? (e) => { e.preventDefault(); setDragOver(false); void attachFiles(e.dataTransfer.files) } : undefined}
    >
      {title && <div style={hdr}>{title}</div>}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((m, i) => (
          <Bubble key={i} msg={m} project={project} onOpen={onOpen} onActionRun={recordActionRun}
            onIntentConfirm={answerIntentConfirm} onAnswerQuestions={answerQuestions}
            onUnlockImport={unlockImport}
            onOpenImport={(part) => setPreview({ cardId: part.cardId, importId: part.importId, objLabel: part.objLabel })} />
        ))}
        {busy && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280' }}>
            <Spinner />
            <span style={{ fontStyle: 'italic' }}>{activity || 'Working…'}</span>
          </div>
        )}
        {error && <div style={{ fontSize: 14, color: '#dc2626' }}>{error}</div>}
        {!hasUser && chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {chips.map((c, i) => (
              <button key={i} onClick={() => send(c)} disabled={busy} style={chip}>{c}</button>
            ))}
          </div>
        )}
      </div>
      {aiUnconfigured ? (
        <div style={{
          borderTop: '1px solid #e5e7eb', padding: '12px 14px', background: '#fffbeb',
          color: '#92400e', fontSize: 14, lineHeight: 1.5,
        }}>
          {config?.ai_notice
            || 'The assistant has no AI configured for your organization yet. Ask your functional '
             + 'administrator to set it up in System administration → AI.'}
          {/* Deterministic scan intents run WITHOUT the AI — keep scanning
              available (attendance kiosks, POS guns) even before it's set up. */}
          {config?.scan_intents && (
            <div style={{ marginTop: 8 }}>
              <button type="button" onClick={() => setScanning(true)} disabled={busy} style={{
                padding: '8px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8,
                border: '1px solid #d1d5db', background: '#fff', color: '#4f46e5', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <ScanIcon /> Scan a code
              </button>
            </div>
          )}
        </div>
      ) : (
      <div style={{ borderTop: '1px solid #e5e7eb', padding: 10 }}>
        {(attached.length > 0 || uploading > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {attached.map((f) => (
              <span key={f.import_id} style={fileChip} title={f.headers.join(', ')}>
                <FileGlyph /> {f.filename}
                <span style={{ color: '#6b7280' }}>· {f.rows} rows × {f.columns} cols</span>
                <button type="button" aria-label={`Remove ${f.filename}`} onClick={() => setAttached((a) => a.filter((x) => x.import_id !== f.import_id))}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280', padding: 0, fontSize: 15, lineHeight: 1 }}>×</button>
              </span>
            ))}
            {uploading > 0 && <span style={{ ...fileChip, color: '#6b7280' }}><Spinner size={11} /> Attaching…</span>}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); send(input) }} style={{ display: 'flex', gap: 8 }}>
          {canImport && (
            <>
              <input ref={fileInputRef} type="file" accept={IMPORT_ACCEPT} multiple style={{ display: 'none' }}
                onChange={(e) => { void attachFiles(e.target.files); e.target.value = '' }} />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}
                title="Attach a data file to import (CSV, Excel, JSON)" aria-label="Attach a data file" style={{
                  padding: '9px 11px', fontSize: 15, borderRadius: 8, border: '1px solid #d1d5db',
                  background: '#fff', color: '#4f46e5', cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                }}>
                <ClipGlyph />
              </button>
            </>
          )}
          {/* `@` names a person, a role, or any master the app has declared
              semantically searchable — and inserts a TYPED token, so the model
              gets a resolved id rather than a name to guess at. */}
          <MentionInput
            value={input}
            onChange={setInput}
            search={mentionSearch}
            placeholder={hasUser ? 'Ask a follow-up… (@ to name someone)' : 'Ask about your data…'}
            style={{ padding: '9px 12px', fontSize: 15, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none' }}
          />
          {config?.scan_intents && !busy && (
            <button type="button" onClick={() => setScanning(true)} title="Scan a QR / barcode" aria-label="Scan a code" style={{
              padding: '9px 11px', fontSize: 15, borderRadius: 8, border: '1px solid #d1d5db',
              background: '#fff', color: '#4f46e5', cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
            }}>
              <ScanIcon />
            </button>
          )}
          {busy ? (
            <button type="button" onClick={stop} title="Stop" aria-label="Stop" style={{
              padding: '9px 16px', fontSize: 15, fontWeight: 600, borderRadius: 8, border: 'none',
              background: '#dc2626', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7,
            }}>
              <span style={{ width: 10, height: 10, background: '#fff', borderRadius: 2, display: 'inline-block' }} />
              Stop
            </button>
          ) : (
            <button type="submit" disabled={(!input.trim() && !attached.length) || uploading > 0} style={{
              padding: '9px 16px', fontSize: 15, fontWeight: 600, borderRadius: 8, border: 'none',
              background: '#4f46e5', color: '#fff', cursor: 'pointer',
              opacity: (!input.trim() && !attached.length) || uploading > 0 ? 0.6 : 1,
            }}>Send</button>
          )}
        </form>
      </div>
      )}
      {preview && (
        <ImportPreview
          project={project}
          importId={preview.importId}
          objLabel={preview.objLabel}
          onClose={(outcome) => {
            if (outcome) recordImportOutcome(preview.cardId, outcome)
            setPreview(null)
          }}
        />
      )}
      {scanning && (
        <ScanCapture
          onScan={(t) => { setScanning(false); send(t, { scan: true }) }}
          onClose={() => setScanning(false)}
        />
      )}
    </div>
  )
}

// Viewfinder glyph for the Scan button (inline so both chat copies stay
// dependency-identical).
function ScanIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <line x1="7" y1="12" x2="17" y2="12" />
    </svg>
  )
}

/** Parts that say where an answer came from, rather than being the answer. */
const CITATION_KINDS = new Set(['documents', 'records'])

function Bubble({ msg, project, onOpen, onActionRun, onIntentConfirm, onAnswerQuestions, onOpenImport, onUnlockImport }: { msg: Msg; project: string; onOpen?: OpenTargetHandler; onActionRun?: (actionId: string, outcome: ActionRunOutcome) => void; onIntentConfirm?: (cid: string, run: boolean) => void; onAnswerQuestions?: (qid: string, answers: Record<string, string>) => void; onOpenImport?: (part: Extract<Part, { kind: 'import_preview' }>) => void; onUnlockImport?: (part: Extract<Part, { kind: 'import_password' }>, password: string, remember: boolean) => Promise<string | null> }) {
  const isUser = msg.role === 'user'
  // Sources go UNDER the answer, whenever they arrived.
  //
  // A citation part is appended the moment the retrieval tool returns, which is
  // before the model has written a word — so a reply rendered in arrival order
  // led with "Where this came from" and buried the answer beneath it. Ordering
  // is a presentation concern, so it is fixed here and not in the stream: the
  // parts array keeps the order it was built in, which is what the streaming
  // patch appends to. The sort is stable, so everything else — text, a visual,
  // an action card — holds its relative position, and each part is keyed by its
  // ORIGINAL index so a streaming bubble is patched rather than remounted.
  const ordered = msg.parts
    .map((part, index) => ({ part, index }))
    .sort((a, b) => Number(CITATION_KINDS.has(a.part.kind))
      - Number(CITATION_KINDS.has(b.part.kind)))
  return (
    <div style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: isUser ? '85%' : '95%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {ordered.map(({ part: p, index: i }) => {
        if (p.kind === 'text') {
          const bubble: CSSProperties = {
            padding: '9px 13px', borderRadius: 12, fontSize: 15, lineHeight: 1.5,
            background: isUser ? '#4f46e5' : '#f3f4f6', color: isUser ? '#fff' : '#1f2937',
            alignSelf: isUser ? 'flex-end' : 'flex-start',
          }
          // The user typed plain text; the assistant answers in markdown --
          // headings, bold, lists and tables, which must render as such.
          return isUser
            ? <div key={i} style={{ ...bubble, whiteSpace: 'pre-wrap' }}>{p.text}</div>
            : <MarkdownText key={i} text={p.text} style={bubble} />
        }
        if (p.kind === 'visual') {
          return <VisualCard key={i} project={project} part={p} />
        }
        if (p.kind === 'action') {
          return <ActionCard key={i} project={project} part={p} onRun={onActionRun} />
        }
        if (p.kind === 'documents') {
          return <DocumentCitations key={i} project={project} part={p} onOpen={onOpen} />
        }
        if (p.kind === 'records') {
          return <RecordCitations key={i} project={project} part={p} onOpen={onOpen} />
        }
        if (p.kind === 'link') {
          return <LinkCard key={i} project={project} part={p} onOpen={onOpen} />
        }
        if (p.kind === 'visual_ref') {
          return <VisualRefCard key={i} project={project} part={p} />
        }
        if (p.kind === 'report') {
          return <ReportCard key={i} project={project} part={p} />
        }
        if (p.kind === 'files') {
          return (
            <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
              {p.files.map((f) => (
                <span key={f.import_id} style={fileChip}><FileGlyph /> {f.filename}
                  <span style={{ color: '#6b7280' }}>· {f.rows} rows</span></span>
              ))}
            </div>
          )
        }
        if (p.kind === 'import_preview') {
          return <ImportCard key={i} part={p} onOpen={() => onOpenImport?.(p)} />
        }
        if (p.kind === 'import_password') {
          return <PdfPasswordCard key={i} part={p}
            onUnlock={(pw, remember) => onUnlockImport ? onUnlockImport(p, pw, remember) : Promise.resolve('Unavailable here.')} />
        }
        if (p.kind === 'questions') {
          return <QuestionsCard key={i} intro={p.intro} questions={p.questions} answered={p.answered}
            onSubmit={(answers) => onAnswerQuestions?.(p.qid, answers)} />
        }
        if (p.kind === 'intent_confirm') {
          return <IntentConfirmCard key={i} part={p} onAnswer={onIntentConfirm} />
        }
        return <RowsTable key={i} columns={p.columns} columnLabels={p.columnLabels} rows={p.rows} rowCount={p.rowCount} />
      })}
    </div>
  )
}

// A rendered visual, with controls to open it fullscreen and to SAVE it to the
// user's private collection (Assistant charts are otherwise ephemeral).
function VisualCard({ project, part }: { project: string; part: Extract<Part, { kind: 'visual' }> }) {
  const [full, setFull] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const canSave = !!(part.spec && part.businessArea)

  const save = async () => {
    setSaving(true); setErr(null)
    try {
      await assistantSaveVisual(project, {
        title: part.title || part.question || '',
        question: part.question || '',
        business_area: part.businessArea,
        spec: part.spec,
        visual_layout: part.visualLayout as unknown as Record<string, unknown>,
        chart_type: part.chartType,
      })
      setSaved(true)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.')
    } finally { setSaving(false) }
  }

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 10, background: '#fff', minWidth: 280 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {part.title && <div style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0 }}>{part.title}</div>}
        <button onClick={() => setFull(true)} style={vcBtn} title="Open fullscreen">⤢ Open</button>
        {canSave && (
          <button onClick={save} disabled={saving || saved} style={{ ...vcBtn, color: saved ? '#16a34a' : '#4f46e5' }}
            title="Save to my visuals">{saved ? '✓ Saved' : saving ? 'Saving…' : '☆ Save'}</button>
        )}
      </div>
      <InsightVisual jsonData={part.jsonData} visualLayout={part.visualLayout} optionsArr={part.optionsArr} chartType={part.chartType} autoHeight style={{ width: '100%' }} />
      {err && <div style={{ fontSize: 13, color: '#dc2626', marginTop: 4 }}>{err}</div>}
      {full && (
        <VisualFullscreen title={part.title} jsonData={part.jsonData} visualLayout={part.visualLayout}
          optionsArr={part.optionsArr} chartType={part.chartType} onClose={() => setFull(false)} />
      )}
    </div>
  )
}

const vcBtn: React.CSSProperties = {
  border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, padding: '3px 8px',
  fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap',
}

/** Href for another screen of THIS app, derived from the current URL: keep
 *  everything up to (and including) the project segment, append the target.
 *  Works in every host the chat mounts in (builder Run `/run/:tenant/:project/*`,
 *  runtime `/:project/:obj`, incl. a server context-path prefix) without
 *  host-specific routing knowledge. Null when the URL has no project segment. */
function objectHref(project: string, target: string): string | null {
  const segs = window.location.pathname.split('/')
  const idx = segs.findIndex((s) => {
    if (s === encodeURIComponent(project)) return true
    try { return decodeURIComponent(s) === project } catch { return false }
  })
  if (idx < 0) return null
  return [...segs.slice(0, idx + 1), encodeURIComponent(target)].join('/')
}

/**
 * A citation's clickable shell — the ONE place a citation decides how it opens.
 *
 * With the host's `onOpen` it is a button that navigates in place, handed the
 * very `OpenTarget` the application-wide search panel hands the same host: a
 * cited record and a search hit for that record therefore land on the same
 * screen, in the same state, and cannot drift apart. Without one (a designer
 * preview, a smart-page embed) it degrades to an ordinary link derived from
 * the current URL, which is all those surfaces can offer.
 *
 * A null target renders neither — the card still shows. Naming what an answer
 * came from is worth more than a link that goes nowhere, which is the same
 * call the search panel makes for a hit it cannot open.
 */
function OpenCitation({ project, target, onOpen, style, children }: {
  project: string
  target: OpenTarget | null
  onOpen?: OpenTargetHandler
  style: React.CSSProperties
  children: React.ReactNode
}) {
  if (target && onOpen) {
    return (
      <button type="button" onClick={() => onOpen(target)}
        style={{ ...style, cursor: 'pointer', font: 'inherit' }}>
        {children}
      </button>
    )
  }
  const base = target ? objectHref(project, target.objName) : null
  const query = target ? new URLSearchParams(target.params).toString() : ''
  const href = base ? (query ? `${base}?${query}` : base) : null
  if (href) return <a href={href} style={style}>{children}</a>
  return <div style={style}>{children}</div>
}

const citeHdr: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#6b7280' }

const citeBox: React.CSSProperties = {
  display: 'block', textAlign: 'left', width: '100%',
  border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 12px',
  background: '#fff', textDecoration: 'none',
}

/**
 * Where an answer came from, and a way to get there.
 *
 * Opens the document IN THE APP — the repository screen it lives in, deep
 * linked to that document (`?doc=<id>`, the same parameter the run surfaces
 * already use) — rather than in a viewer bolted onto the chat. The reader ends
 * up on the real document under the real permissions, with its history, its
 * details and whatever they are being asked to do about it.
 */
function DocumentCitations({ project, part, onOpen }: {
  project: string
  part: Extract<Part, { kind: 'documents' }>
  onOpen?: OpenTargetHandler
}) {
  // Which passages are unfolded, by position in this answer's list. Local
  // because a chat message is never rebuilt — the parts of a reply are appended
  // and then left alone, so what the reader opened here stays open.
  const [openPassages, setOpenPassages] = useState<number[]>([])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 280 }}>
      <div style={citeHdr}>
        {part.citations.length === 1 ? 'Where this came from'
          : `Where this came from (${part.citations.length})`}
      </div>
      {part.citations.map((citation, index) => {
        // No repository stamped ⇒ nowhere reliable to send them.
        const target = documentTarget(citation.repo_obj || '', citation.document_id)
        const detail = [
          citation.version ? `Version ${citation.version}` : '',
          citation.effective_date ? `in force since ${citation.effective_date}` : '',
          citation.page ? `page ${citation.page}` : '',
        ].filter(Boolean).join(' · ')
        const passageOpen = openPassages.includes(index)
        return (
          <div key={`${citation.document_id}-${index}`} style={citeBox}>
            {/* The document's NAME, not a control. Leaving the answer to open
                a document is a deliberate act, so it is asked for by name —
                "Read document" below — rather than by clicking a card that
                gives no sign it will navigate away. */}
            <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
              {citation.title || 'Untitled document'}
            </div>
            {detail ? (
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{detail}</div>
            ) : null}
            {citation.excerpt ? (
              <>
                {/* Folded by default: which documents an answer rests on is
                    what this list is for, and a paragraph quoted under each
                    one buries the next before it can be seen. */}
                <button
                  type="button"
                  aria-expanded={passageOpen}
                  aria-controls={`cite-${citation.document_id}-${index}`}
                  onClick={() => setOpenPassages((prior) => (
                    prior.includes(index) ? prior.filter((i) => i !== index)
                      : [...prior, index]))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    marginTop: 6, padding: '2px 0', border: 0, background: 'none',
                    color: '#6b7280', fontSize: 13, fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <ChevronIcon open={passageOpen} />
                  {passageOpen ? 'Hide the cited passage' : 'Show the cited passage'}
                </button>
                <div id={`cite-${citation.document_id}-${index}`} hidden={!passageOpen}
                  style={{
                    marginTop: 6, paddingLeft: 10, borderLeft: '3px solid #e5e7eb',
                    fontSize: 14, lineHeight: 1.55, color: '#374151',
                  }}>
                  {citation.excerpt}
                </div>
              </>
            ) : null}
            {/* Nothing to click when there is nowhere reliable to send them —
                the card still names the source, which is the same call made
                before this was a button of its own. The font is set on the
                wrapper: `OpenCitation` gives its button `font: inherit`, so
                this is what keeps the button and the link paths identical. */}
            {target ? (
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600 }}>
                <OpenCitation project={project} target={target} onOpen={onOpen}
                  style={readDocStyle}>
                  Read document
                </OpenCitation>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** The disclosure twist for a cited passage. Inline so both chat copies stay
 *  dependency-identical. */
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden
      style={{
        flexShrink: 0, transition: 'transform 150ms ease',
        transform: open ? 'rotate(90deg)' : 'none',
      }}>
      <path d="M6 3l4.5 5L6 13" />
    </svg>
  )
}

const readDocStyle: React.CSSProperties = {
  display: 'inline-block', border: 0, background: 'none', padding: 0,
  color: '#4f46e5', textDecoration: 'none', textAlign: 'left',
}

/**
 * The records the answer is ABOUT.
 *
 * The resolver has always matched the name a person said to the exact stored
 * row, and the model wrote its answer from that — but only the model ever saw
 * which row it was. These chips close that loop: "the artist ARR" becomes the
 * artist record, one click away, on the screen search would have opened it on.
 * A record whose object is indexed as a table source has no editor, so it is
 * named without being offered as a link.
 */
function RecordCitations({ project, part, onOpen }: {
  project: string
  part: Extract<Part, { kind: 'records' }>
  onOpen?: OpenTargetHandler
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
      <div style={citeHdr}>{part.citations.length === 1 ? 'Record' : 'Records'}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {part.citations.map((citation, index) => {
          const target = citation.openable === false
            ? null : recordTarget(citation.obj_name, citation.record_key)
          return (
            <OpenCitation key={`${citation.obj_name}-${citation.record_key}-${index}`}
              project={project} target={target} onOpen={onOpen} style={recordChip}>
              <span style={{ fontWeight: 600, color: target ? '#4f46e5' : '#111827' }}>
                {citation.title || citation.record_key}
              </span>
              <span style={{ color: '#6b7280' }}>{prettyHeader(citation.obj_name)}</span>
              {target ? <span aria-hidden style={{ color: '#4f46e5' }}>→</span> : null}
            </OpenCitation>
          )
        })}
      </div>
    </div>
  )
}

const recordChip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  border: '1px solid #e5e7eb', borderRadius: 999, background: '#fff',
  fontSize: 14, textDecoration: 'none', textAlign: 'left',
}

// A navigate-intent card — one click opens the target screen of the app.
function LinkCard({ project, part, onOpen }: {
  project: string
  part: Extract<Part, { kind: 'link' }>
  onOpen?: OpenTargetHandler
}) {
  const target = screenTarget(part.target)
  // URL shape unknown here and no host navigation ⇒ the text guidance stands alone.
  if (!target || (!onOpen && !objectHref(project, target.objName))) return null
  return (
    <OpenCitation project={project} target={target} onOpen={onOpen} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 13px',
      border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff',
      fontSize: 15, fontWeight: 600, color: '#4f46e5', textDecoration: 'none',
    }}>
      Open {part.title} <span aria-hidden>→</span>
    </OpenCitation>
  )
}

// Confirmation card for a deterministic intent run — the user approves or
// declines; approval re-runs the intent with confirmed=true.
function IntentConfirmCard({ part, onAnswer }: {
  part: Extract<Part, { kind: 'intent_confirm' }>
  onAnswer?: (cid: string, run: boolean) => void
}) {
  const answered = !!part.resolved
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#fff', minWidth: 280 }}>
      <div style={{ fontSize: 15, lineHeight: 1.5, marginBottom: answered ? 0 : 10 }}>{part.prompt}</div>
      {!answered ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onAnswer?.(part.cid, true)} style={{
            padding: '7px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
            background: '#16a34a', color: '#fff', cursor: 'pointer',
          }}>Yes, run it</button>
          <button onClick={() => onAnswer?.(part.cid, false)} style={{
            padding: '7px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8,
            border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer',
          }}>Cancel</button>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: part.resolved === 'ran' ? '#16a34a' : '#6b7280', marginTop: 6 }}>
          {part.resolved === 'ran' ? '✓ Confirmed' : 'Cancelled'}
        </div>
      )}
    </div>
  )
}

// A DEFINED Visual (V) referenced by an intent — fetched through the secured
// visual-data route (which enforces the caller's read rights) and rendered
// with the app's real visual engine.
function VisualRefCard({ project, part }: { project: string; part: Extract<Part, { kind: 'visual_ref' }> }) {
  const [data, setData] = useState<VisualDataPayload | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [full, setFull] = useState(false)
  const paramsKey = JSON.stringify(part.params || {})
  useEffect(() => {
    let gone = false
    setData(null); setErr(null)
    fetchVisualData({ project, obj_name: part.objName, params: part.params })
      .then((d) => { if (!gone) setData(d) })
      .catch((e: unknown) => {
        if (gone) return
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setErr(detail || (e instanceof Error ? e.message : 'Could not load the visual.'))
      })
    return () => { gone = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, part.objName, paramsKey])

  const chartType = (data?.visualLayout as { className?: string } | undefined)?.className
  const title = part.title || part.objName
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 10, background: '#fff', minWidth: 280 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0 }}>{title}</div>
        {data && <button onClick={() => setFull(true)} style={vcBtn} title="Open fullscreen">⤢ Open</button>}
      </div>
      {err && <div style={{ fontSize: 13, color: '#dc2626' }}>{err}</div>}
      {!err && !data && <div style={{ fontSize: 13, color: '#6b7280', padding: '14px 4px' }}>Loading…</div>}
      {data?.needsInput && (
        <div style={{ fontSize: 13, color: '#92400e' }}>
          This visual needs more inputs{data.promptVars?.length ? ` (${data.promptVars.map((v) => (v as { name?: string }).name || '').filter(Boolean).join(', ')})` : ''} — open it from the app's navigation to fill them.
        </div>
      )}
      {data && !data.needsInput && (
        <InsightVisual jsonData={data.jsonData} visualLayout={data.visualLayout}
          optionsArr={data.optionsArr || []} chartType={chartType} autoHeight style={{ width: '100%' }} />
      )}
      {full && data && !data.needsInput && (
        <VisualFullscreen title={title} jsonData={data.jsonData} visualLayout={data.visualLayout}
          optionsArr={data.optionsArr || []} chartType={chartType} onClose={() => setFull(false)} />
      )}
    </div>
  )
}

// A Jasper Report (R) referenced by an intent — rendered to HTML through the
// secured report-data route and shown inline (fullscreen on demand).
function ReportCard({ project, part }: { project: string; part: Extract<Part, { kind: 'report' }> }) {
  const [html, setHtml] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [full, setFull] = useState(false)
  const paramsKey = JSON.stringify(part.params || {})
  useEffect(() => {
    let gone = false
    setHtml(null); setErr(null)
    runReportJob({ project, obj_name: part.objName, params: part.params, output_format: 'html' })
      .then(async (r) => {
        if (gone) return
        if (r.needsInput) {
          setErr(`The report needs more parameters: ${(r.missingParams || []).join(', ') || 'see its screen'}.`)
          return
        }
        if (!r.blob) {
          setErr('The report produced no inline output.')
          return
        }
        const text = await r.blob.text()
        if (!gone) setHtml(text)
      })
      .catch((e: unknown) => {
        if (gone) return
        const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setErr(detail || (e instanceof Error ? e.message : 'Could not render the report.'))
      })
    return () => { gone = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, part.objName, paramsKey])

  const title = part.title || part.objName
  const frame = (h: number | string) => (
    <iframe title={title} sandbox="" srcDoc={html || ''} style={{
      width: '100%', height: h, border: 'none', background: '#fff', borderRadius: 8,
    }} />
  )
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 10, background: '#fff', minWidth: 300 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0 }}>{title}</div>
        {html && <button onClick={() => setFull(true)} style={vcBtn} title="Open fullscreen">⤢ Open</button>}
      </div>
      {err && <div style={{ fontSize: 13, color: '#dc2626' }}>{err}</div>}
      {!err && !html && <div style={{ fontSize: 13, color: '#6b7280', padding: '14px 4px' }}>Rendering…</div>}
      {html && frame(340)}
      {full && html && createPortal(
        <div style={rcOverlay} onClick={() => setFull(false)}>
          <div style={rcSheet} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{title}</span>
              <button onClick={() => setFull(false)} style={{ border: 'none', background: 'transparent', fontSize: 17, cursor: 'pointer', color: '#6b7280' }} aria-label="Close">✕</button>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>{frame('100%')}</div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

const rcOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9800,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
}
const rcSheet: React.CSSProperties = {
  width: 'min(1100px, 96vw)', height: 'min(760px, 92vh)', background: '#fff', color: '#1f2937',
  borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column',
  boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
}

// Proposed data-change action — the user reviews the values and clicks Run; the
// server executes it through the object's action API ONLY if authorized.
function ActionCard({ project, part, onRun }: { project: string; part: Extract<Part, { kind: 'action' }>; onRun?: (actionId: string, outcome: ActionRunOutcome) => void }) {
  if (part.authoring) return <AuthoringActionCard project={project} part={part} onRun={onRun} />
  return <PlainActionCard project={project} part={part} onRun={onRun} />
}

// A record whose content the Assistant wrote (or one with a draft/live
// lifecycle): the shared review card, wired to this host's endpoints.
function AuthoringActionCard({ project, part, onRun }: { project: string; part: Extract<Part, { kind: 'action' }>; onRun?: (actionId: string, outcome: ActionRunOutcome) => void }) {
  const authoring = part.authoring as AuthoringInfo
  return (
    <ComposedContentCard
      objLabel={part.objLabel || part.objName}
      action={part.action}
      summary={part.summary}
      columns={part.columns}
      values={part.ranValues || part.values}
      authoring={authoring}
      ran={part.ran}
      ranPublished={part.ranPublished}
      runError={part.runError}
      onRun={async (values, publish) => {
        try {
          const r = await assistantRunAction(project, { obj_name: part.objName, action: part.action, values, publish })
          onRun?.(part.actionId, r.ok
            ? { ran: 'ok', ranValues: { ...values }, ranPublished: publish, result: (r.result as Record<string, unknown>) ?? {} }
            : { ran: 'error', ranValues: { ...values }, ranPublished: publish, runError: r.error || 'the server refused it without giving a reason.' })
          return { ok: r.ok, error: r.error, result: r.result }
        } catch (e) {
          const err = describeRequestError(e, 'The save failed.')
          onRun?.(part.actionId, { ran: 'error', ranValues: { ...values }, ranPublished: publish, runError: err })
          return { ok: false, error: err }
        }
      }}
      onRefine={authoring.composed.length ? (values, feedback) =>
        assistantComposeRefine(project, { obj_name: part.objName, values, feedback }) : undefined}
    />
  )
}

function PlainActionCard({ project, part, onRun }: { project: string; part: Extract<Part, { kind: 'action' }>; onRun?: (actionId: string, outcome: ActionRunOutcome) => void }) {
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...part.values }))
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<AssistantRunResult | null>(null)
  const colFor = (k: string) => part.columns.find((c) => c.key === k)
  const labelFor = (k: string) => colFor(k)?.label || k
  const requiredFor = (k: string) => { const c = colFor(k); return !!(c?.required && !c?.auto) }
  const isDelete = part.action.toLowerCase() === 'delete'
  const isAdd = part.action.toLowerCase() === 'add'
  // For an ADD, always offer the object's data-entry fields (non-auto columns) so
  // the user can fill required fields the agent omitted — prefilled with whatever
  // it proposed. For edit/delete/custom, edit just the fields the agent proposed.
  const keys = isAdd
    ? [
        ...part.columns.filter((c) => !c.auto).map((c) => c.key),
        ...Object.keys(values).filter((k) => !part.columns.some((c) => !c.auto && c.key === k)),
      ]
    : (Object.keys(values).length ? Object.keys(values) : part.columns.map((c) => c.key))
  const setField = (k: string, v: string) => setValues((vs) => ({ ...vs, [k]: v }))

  const actionLabel = part.action.charAt(0).toUpperCase() + part.action.slice(1)
  const runColor = isDelete ? '#dc2626' : '#16a34a'

  const run = async () => {
    setBusy(true); setResult(null)
    try {
      const r = await assistantRunAction(project, { obj_name: part.objName, action: part.action, values })
      setResult(r)
      onRun?.(part.actionId, r.ok
        ? { ran: 'ok', ranValues: { ...values }, result: (r.result as Record<string, unknown>) ?? {} }
        : { ran: 'error', ranValues: { ...values }, runError: r.error || 'failed' })
    } catch (e) {
      const err = e instanceof Error ? e.message : 'Failed'
      setResult({ ok: false, action: part.action, obj_name: part.objName, error: err })
      onRun?.(part.actionId, { ran: 'error', ranValues: { ...values }, runError: err })
    } finally { setBusy(false) }
  }

  const done = result?.ok
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff', minWidth: 300 }}>
      <div style={{ padding: '8px 12px', fontSize: 14, fontWeight: 600, background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        {actionLabel} → {part.objLabel || part.objName}
        {part.isCustom && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>(custom action)</span>}
        {part.summary && <div style={{ fontWeight: 400, fontSize: 13, color: '#6b7280', marginTop: 2 }}>{part.summary}</div>}
      </div>
      <div style={{ padding: 10, display: 'grid', gap: 8 }}>
        {keys.length === 0 && <div style={{ fontSize: 13, color: '#6b7280' }}>No values — this action takes no inputs.</div>}
        {keys.map((k) => (
          <label key={k} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <span style={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {labelFor(k)}{requiredFor(k) && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
            </span>
            <input value={values[k] == null ? '' : String(values[k])} disabled={busy || !!done}
              onChange={(e) => setField(k, e.currentTarget.value)}
              style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderTop: '1px solid #e5e7eb' }}>
        {!done && (
          <button onClick={run} disabled={busy} style={{
            padding: '8px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
            background: runColor, color: '#fff', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
          }}>{busy ? 'Running…' : `${actionLabel} ${part.objLabel || part.objName}`}</button>
        )}
        {result && (
          <div style={{ fontSize: 14, color: result.ok ? '#16a34a' : '#b45309' }}>
            {result.ok ? `Done — ${part.action} ran successfully.` : `Couldn't run: ${result.error || 'failed'}`}
          </div>
        )}
      </div>
    </div>
  )
}

function RowsTable({ columns, columnLabels, rows, rowCount }: { columns: string[]; columnLabels?: Record<string, string>; rows: Record<string, unknown>[]; rowCount: number }) {
  const cols = columns.length ? columns : (rows[0] ? Object.keys(rows[0]).filter((k) => k !== 'id') : [])
  const shown = rows.slice(0, 50)
  // Columns whose every value is a pure calendar date (midnight timestamps
  // like "2026-05-03 00:00:00") display without the meaningless time part.
  const dateOnly = new Set(cols.filter((c) => isDateOnlyColumn(shown, c)))
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <div style={{ overflowX: 'auto', maxHeight: 320 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 14, width: '100%' }}>
          <thead><tr>{cols.map((c) => <th key={c} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', position: 'sticky', top: 0 }}>{columnLabels?.[c] ?? prettyHeader(c)}</th>)}</tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>{cols.map((c) => (
                <td key={c} style={{ padding: '5px 10px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                  {isImageUrl(r[c]) ? <CellImage url={r[c] as string} />
                    : dateOnly.has(c) ? fmt(r[c]).trim().slice(0, 10) : fmt(r[c])}
                </td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {rowCount > shown.length && <div style={{ padding: '6px 10px', fontSize: 13, color: '#6b7280' }}>Showing {shown.length} of {rowCount} rows</div>}
    </div>
  )
}

// "2026-05-03", "2026-05-03 00:00:00" or "2026-05-03T00:00:00" — a value whose
// time part carries no information.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}([T ]00:00(:00(\.0+)?)?Z?)?$/

function isDateOnlyColumn(rows: Record<string, unknown>[], col: string): boolean {
  let seen = false
  for (const r of rows) {
    const v = r[col]
    if (v === null || v === undefined || v === '') continue
    if (typeof v !== 'string' || !DATE_ONLY_RE.test(v.trim())) return false
    seen = true
  }
  return seen
}

// Self-contained spinner (SMIL rotate — no CSS keyframe dependency, so it works
// identically in the builder and the standalone runtime bundle).
function Spinner({ size = 13, color = '#6b7280' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }} aria-hidden>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="3" strokeLinecap="round" strokeDasharray="42 42" opacity="0.9">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  )
}

/**
 * Is this cell the URL of a stored image?
 *
 * An image column stores the served url of its upload, so a result table used
 * to print `/api/…/raw?file=…&name=photo.png` across the row — text that tells
 * the reader nothing and is useless on a phone. The classification is
 * `detectFileKind`, the same one the upload widget uses to decide it is holding
 * an image, so the Assistant and the form agree on what an image is.
 */
function isImageUrl(v: unknown): v is string {
  if (typeof v !== 'string') return false
  const s = v.trim()
  if (!s || /\s/.test(s)) return false
  if (s.startsWith('data:image/')) return true
  if (!(s.startsWith('/') || s.startsWith('http://') || s.startsWith('https://'))) return false
  return detectFileKind(s) === 'image'
}

/** A thumbnail that opens the full-size file. A url that will not load falls
 *  back to the file's name — better than a broken-image glyph in a table. */
function CellImage({ url }: { url: string }) {
  const [broken, setBroken] = useState(false)
  if (broken) return <span style={{ color: '#6b7280' }}>{displayNameForUrl(url)}</span>
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title="Open full size"
      style={{ display: 'inline-block', lineHeight: 0 }}>
      <img src={url} alt="" onError={() => setBroken(true)} style={{
        height: 40, width: 'auto', maxWidth: 140, objectFit: 'contain',
        borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff',
      }} />
    </a>
  )
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return v.toLocaleString()
  return String(v)
}

// Safety net for a table header the server didn't label: a raw identifier
// (ALL-CAPS or snake_case) becomes Title Case; a real caption is left as-is.
function prettyHeader(c: string): string {
  const s = String(c ?? '').trim()
  if (!s) return s
  if (s.includes('_') || s === s.toUpperCase()) {
    return s.replace(/_/g, ' ').split(/\s+/).filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
  }
  return s
}

const hdr: React.CSSProperties = { padding: '11px 14px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 16 }
const chip: React.CSSProperties = { padding: '6px 12px', fontSize: 14, borderRadius: 999, border: '1px solid #d1d5db', background: '#fff', color: '#4f46e5', cursor: 'pointer' }

const MODE_TEXT: Record<string, string> = {
  add: 'add as new records', edit: 'update existing records', upsert: 'add or update by key',
}

/** The chat's summary of a prepared file import; opens the side-by-side preview. */
function PdfPasswordCard({ part, onUnlock }: {
  part: Extract<Part, { kind: 'import_password' }>
  onUnlock: (password: string, remember: boolean) => Promise<string | null>
}) {
  const [pw, setPw] = useState('')
  const [remember, setRemember] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  if (part.unlocked) {
    return (
      <div style={{ fontSize: 13.5, color: '#047857', fontWeight: 600 }}>
        PDF unlocked{part.note ? ` — ${part.note}` : ''}
      </div>
    )
  }
  const submit = async () => {
    if (!pw || busy) return
    setBusy(true)
    setErr(null)
    const problem = await onUnlock(pw, remember)
    setBusy(false)
    if (problem) setErr(problem)
    else setPw('')
  }
  return (
    <div style={{ border: '1px solid #fcd34d', borderRadius: 10, padding: 12, background: '#fffbeb',
      display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>This PDF is password-protected</div>
      <div style={{ fontSize: 13.5, color: '#4b5563' }}>
        Enter its password here — it is used to open the file and is never sent to the AI.
      </div>
      <input type="password" value={pw} autoComplete="off" onChange={(e) => setPw(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit() }} aria-label="PDF password"
        style={{ padding: '7px 10px', fontSize: 14, borderRadius: 8, border: '1px solid #d1d5db' }} />
      {part.canRemember !== false && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#374151' }}>
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          Remember this password for this sender (stored encrypted)
        </label>
      )}
      {err && <div style={{ fontSize: 13.5, color: '#b91c1c' }}>{err}</div>}
      <div>
        <button onClick={() => void submit()} disabled={!pw || busy} style={{
          padding: '7px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
          background: '#4f46e5', color: '#fff', cursor: pw && !busy ? 'pointer' : 'default', opacity: pw ? 1 : 0.6,
        }}>{busy ? 'Opening…' : 'Unlock and read'}</button>
      </div>
    </div>
  )
}

function ImportCard({ part, onOpen }: { part: Extract<Part, { kind: 'import_preview' }>; onOpen: () => void }) {
  const c = part.counts
  const o = part.outcome
  return (
    <div style={{ border: '1px solid #c7d2fe', borderRadius: 10, padding: 12, background: '#f8f9ff', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FileGlyph />
        <div style={{ fontWeight: 700, fontSize: 15 }}>Import into {part.objLabel || part.objName}</div>
      </div>
      <div style={{ fontSize: 13.5, color: '#4b5563' }}>
        {part.filename && <>{part.filename} · </>}{MODE_TEXT[part.mode] || part.mode}
        {part.summary && <div style={{ marginTop: 2 }}>{part.summary}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 13 }}>
        <span style={statPill('#dbeafe', '#1d4ed8')}>{c.records} record{c.records === 1 ? '' : 's'}</span>
        <span style={statPill('#e0e7ff', '#3730a3')}>{c.new} new</span>
        {c.update > 0 && <span style={statPill('#fef3c7', '#a16207')}>{c.update} update</span>}
        {(c.unchanged ?? 0) > 0 && <span style={statPill('#f1f5f9', '#475569')}>{c.unchanged} unchanged</span>}
        {c.errors > 0 && <span style={statPill('#fee2e2', '#b91c1c')}>{c.errors} error{c.errors === 1 ? '' : 's'}</span>}
        {c.warnings > 0 && <span style={statPill('#ffedd5', '#c2410c')}>{c.warnings} warning{c.warnings === 1 ? '' : 's'}</span>}
      </div>
      {part.topIssues.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151' }}>
          {part.topIssues.slice(0, 4).map((it, i) => (
            <li key={i} style={{ color: it.severity === 'error' ? '#b91c1c' : '#92400e' }}>
              {it.line != null ? `Line ${it.line}: ` : ''}{it.message}
            </li>
          ))}
          {c.errors + c.warnings > 4 && <li style={{ listStyle: 'none', color: '#6b7280' }}>…and more in the preview</li>}
        </ul>
      )}
      {o && (
        <div style={{ fontSize: 13.5, fontWeight: 600, color: o.failed || o.pending ? '#92400e' : '#047857' }}>
          Saved {o.saved} of {o.total}{o.failed ? ` · ${o.failed} failed` : ''}{o.pending ? ` · ${o.pending} not saved` : ''}{o.unchanged ? ` · ${o.unchanged} unchanged` : ''}{o.leftOut ? ` · ${o.leftOut} left out` : ''}
        </div>
      )}
      <div>
        <button onClick={onOpen} style={{
          padding: '7px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
          background: '#4f46e5', color: '#fff', cursor: 'pointer',
        }}>{o ? 'Reopen preview' : 'Open preview'}</button>
      </div>
    </div>
  )
}

function statPill(bg: string, fg: string): React.CSSProperties {
  return { background: bg, color: fg, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }
}

const fileChip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', fontSize: 13,
  borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#1f2937',
}

// Inline glyphs so both chat copies stay dependency-identical.
function FileGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h8M12 11v8" />
    </svg>
  )
}

function ClipGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  )
}
